import React, { useState, useEffect, useCallback } from 'react';
import { getSoundManager, SFX_KEYS } from '../game/systems/SoundManager';

// ============ TYPES ============

interface ResourceDrop {
  itemId: string;
  chance: number;
  minQuantity: number;
  maxQuantity: number;
}

interface ResourceNodeResearchData {
  nodeId: string;
  nodeType: string;
  name: string;
  description: string;
  requiredLevel: number;
  requiredSkill: 'salvaging' | 'mining' | 'foraging';
  harvestTime: number;
  maxHarvests: number;
  drops: ResourceDrop[];
}

interface ResourceNodeResearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string | null;
  nodeType: string | null;
  nodePosition: { x: number; y: number } | null;
  playerPosition: { x: number; y: number } | null;
}

// ============ CONSTANTS ============

const RESEARCH_DURATION_MS = 2500; // 2.5 seconds for resource nodes

// Resource node definitions (simplified from server definitions)
const RESOURCE_NODE_INFO: Record<string, { name: string; description: string; requiredLevel: number; requiredSkill: 'salvaging' | 'mining' | 'foraging'; harvestTime: number; maxHarvests: number; drops: ResourceDrop[] }> = {
  // Salvaging nodes
  shipwreck_debris: {
    name: 'Shipwreck Debris',
    description: 'Floating wreckage from a sunken vessel.',
    requiredLevel: 1,
    requiredSkill: 'salvaging',
    harvestTime: 5,
    maxHarvests: 3,
    drops: [
      { itemId: 'wood_plank', chance: 0.8, minQuantity: 2, maxQuantity: 5 },
      { itemId: 'iron_ore', chance: 0.5, minQuantity: 1, maxQuantity: 3 },
      { itemId: 'rope', chance: 0.6, minQuantity: 1, maxQuantity: 3 },
      { itemId: 'cloth', chance: 0.4, minQuantity: 1, maxQuantity: 2 },
    ],
  },
  sunken_rowboat: {
    name: 'Sunken Rowboat',
    description: 'A small capsized boat with salvageable parts.',
    requiredLevel: 10,
    requiredSkill: 'salvaging',
    harvestTime: 4,
    maxHarvests: 2,
    drops: [
      { itemId: 'wood_plank', chance: 0.9, minQuantity: 3, maxQuantity: 6 },
      { itemId: 'rope', chance: 0.7, minQuantity: 2, maxQuantity: 4 },
      { itemId: 'gears', chance: 0.25, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'barnacle_chest', chance: 0.1, minQuantity: 1, maxQuantity: 1 },
    ],
  },
  merchant_wreck: {
    name: 'Merchant Wreck',
    description: 'Remains of a trading vessel. Rich in trade goods.',
    requiredLevel: 20,
    requiredSkill: 'salvaging',
    harvestTime: 6,
    maxHarvests: 3,
    drops: [
      { itemId: 'cloth', chance: 0.8, minQuantity: 3, maxQuantity: 6 },
      { itemId: 'rope', chance: 0.7, minQuantity: 2, maxQuantity: 4 },
      { itemId: 'gold_thread', chance: 0.3, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'glass_lens', chance: 0.25, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'waterlogged_map', chance: 0.08, minQuantity: 1, maxQuantity: 1 },
    ],
  },
  abandoned_cargo: {
    name: 'Abandoned Cargo',
    description: 'Floating crates from a merchant vessel.',
    requiredLevel: 25,
    requiredSkill: 'salvaging',
    harvestTime: 6,
    maxHarvests: 2,
    drops: [
      { itemId: 'gears', chance: 0.4, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'steel_ingot', chance: 0.3, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'glass_lens', chance: 0.2, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'cloth', chance: 0.5, minQuantity: 2, maxQuantity: 4 },
      { itemId: 'gold_thread', chance: 0.15, minQuantity: 1, maxQuantity: 1 },
    ],
  },
  naval_wreck: {
    name: 'Naval Wreck',
    description: 'Wreckage of a military vessel. Contains naval supplies.',
    requiredLevel: 30,
    requiredSkill: 'salvaging',
    harvestTime: 8,
    maxHarvests: 3,
    drops: [
      { itemId: 'cannon_powder', chance: 0.7, minQuantity: 2, maxQuantity: 4 },
      { itemId: 'iron_ore', chance: 0.6, minQuantity: 2, maxQuantity: 5 },
      { itemId: 'gears', chance: 0.5, minQuantity: 1, maxQuantity: 3 },
      { itemId: 'steel_ingot', chance: 0.35, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'naval_insignia', chance: 0.12, minQuantity: 1, maxQuantity: 1 },
    ],
  },
  ancient_ruins: {
    name: 'Ancient Ruins',
    description: 'Mysterious sunken structures from a lost civilization.',
    requiredLevel: 50,
    requiredSkill: 'salvaging',
    harvestTime: 12,
    maxHarvests: 2,
    drops: [
      { itemId: 'ancient_mechanism', chance: 0.3, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'enchanted_thread', chance: 0.25, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'rare_metal', chance: 0.4, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'glass_lens', chance: 0.35, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'gold_thread', chance: 0.3, minQuantity: 1, maxQuantity: 1 },
    ],
  },
  // Mining nodes
  stone_quarry: {
    name: 'Stone Quarry',
    description: 'Loose stone and exposed ore fragments.',
    requiredLevel: 1,
    requiredSkill: 'mining',
    harvestTime: 5,
    maxHarvests: 3,
    drops: [
      { itemId: 'iron_ore', chance: 0.65, minQuantity: 1, maxQuantity: 3 },
      { itemId: 'copper_ore', chance: 0.5, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'stone', chance: 0.8, minQuantity: 2, maxQuantity: 4 },
    ],
  },
  tin_deposit: {
    name: 'Tin Deposit',
    description: 'A vein of tin ore, essential for bronze crafting.',
    requiredLevel: 10,
    requiredSkill: 'mining',
    harvestTime: 5,
    maxHarvests: 3,
    drops: [
      { itemId: 'tin_ore', chance: 0.85, minQuantity: 2, maxQuantity: 4 },
      { itemId: 'copper_ore', chance: 0.4, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'stone', chance: 0.3, minQuantity: 1, maxQuantity: 2 },
    ],
  },
  mineral_outcrop: {
    name: 'Mineral Outcrop',
    description: 'A rocky formation with exposed ore veins.',
    requiredLevel: 15,
    requiredSkill: 'mining',
    harvestTime: 6,
    maxHarvests: 4,
    drops: [
      { itemId: 'iron_ore', chance: 0.7, minQuantity: 2, maxQuantity: 4 },
      { itemId: 'copper_ore', chance: 0.5, minQuantity: 1, maxQuantity: 3 },
      { itemId: 'rare_metal', chance: 0.12, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'sulfur', chance: 0.25, minQuantity: 1, maxQuantity: 2 },
    ],
  },
  silver_vein: {
    name: 'Silver Vein',
    description: 'A glittering vein of silver ore.',
    requiredLevel: 20,
    requiredSkill: 'mining',
    harvestTime: 7,
    maxHarvests: 3,
    drops: [
      { itemId: 'silver_ore', chance: 0.8, minQuantity: 2, maxQuantity: 4 },
      { itemId: 'iron_ore', chance: 0.4, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'gem_rough', chance: 0.15, minQuantity: 1, maxQuantity: 1 },
    ],
  },
  gold_deposit: {
    name: 'Gold Deposit',
    description: 'A rich deposit of gold ore. Highly valuable.',
    requiredLevel: 25,
    requiredSkill: 'mining',
    harvestTime: 8,
    maxHarvests: 2,
    drops: [
      { itemId: 'gold_ore', chance: 0.75, minQuantity: 1, maxQuantity: 3 },
      { itemId: 'silver_ore', chance: 0.4, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'gem_rough', chance: 0.2, minQuantity: 1, maxQuantity: 1 },
    ],
  },
  coal_seam: {
    name: 'Coal Seam',
    description: 'A black seam of coal, useful for smelting.',
    requiredLevel: 30,
    requiredSkill: 'mining',
    harvestTime: 6,
    maxHarvests: 4,
    drops: [
      { itemId: 'coal', chance: 0.9, minQuantity: 3, maxQuantity: 6 },
      { itemId: 'stone', chance: 0.3, minQuantity: 1, maxQuantity: 2 },
    ],
  },
  volcanic_vent: {
    name: 'Volcanic Vent',
    description: 'A vent spewing volcanic minerals and rare metals.',
    requiredLevel: 40,
    requiredSkill: 'mining',
    harvestTime: 10,
    maxHarvests: 2,
    drops: [
      { itemId: 'volcanic_glass', chance: 0.4, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'rare_metal', chance: 0.5, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'sulfur', chance: 0.6, minQuantity: 2, maxQuantity: 4 },
    ],
  },
  // Foraging nodes
  driftwood_cluster: {
    name: 'Driftwood Cluster',
    description: 'A collection of weathered wood washed ashore.',
    requiredLevel: 1,
    requiredSkill: 'foraging',
    harvestTime: 4,
    maxHarvests: 3,
    drops: [
      { itemId: 'wood_plank', chance: 0.8, minQuantity: 1, maxQuantity: 3 },
      { itemId: 'fiber', chance: 0.5, minQuantity: 1, maxQuantity: 2 },
    ],
  },
  coconut_palm: {
    name: 'Coconut Palm',
    description: 'A palm tree with harvestable coconuts.',
    requiredLevel: 5,
    requiredSkill: 'foraging',
    harvestTime: 3,
    maxHarvests: 4,
    drops: [
      { itemId: 'coconut', chance: 0.9, minQuantity: 2, maxQuantity: 4 },
      { itemId: 'fiber', chance: 0.4, minQuantity: 1, maxQuantity: 2 },
    ],
  },
  seaweed_bed: {
    name: 'Seaweed Bed',
    description: 'A dense bed of seaweed growing in shallow waters.',
    requiredLevel: 10,
    requiredSkill: 'foraging',
    harvestTime: 4,
    maxHarvests: 5,
    drops: [
      { itemId: 'seaweed', chance: 0.85, minQuantity: 2, maxQuantity: 5 },
      { itemId: 'fiber', chance: 0.6, minQuantity: 1, maxQuantity: 3 },
    ],
  },
  herb_patch: {
    name: 'Herb Patch',
    description: 'A patch of medicinal herbs and plants.',
    requiredLevel: 15,
    requiredSkill: 'foraging',
    harvestTime: 5,
    maxHarvests: 3,
    drops: [
      { itemId: 'herbs', chance: 0.8, minQuantity: 2, maxQuantity: 4 },
      { itemId: 'fiber', chance: 0.5, minQuantity: 1, maxQuantity: 2 },
    ],
  },
  fruit_grove: {
    name: 'Fruit Grove',
    description: 'A grove of fruit-bearing trees.',
    requiredLevel: 20,
    requiredSkill: 'foraging',
    harvestTime: 4,
    maxHarvests: 4,
    drops: [
      { itemId: 'fruit', chance: 0.9, minQuantity: 3, maxQuantity: 6 },
      { itemId: 'fiber', chance: 0.4, minQuantity: 1, maxQuantity: 2 },
    ],
  },
  mushroom_colony: {
    name: 'Mushroom Colony',
    description: 'A cluster of various mushrooms growing in damp conditions.',
    requiredLevel: 25,
    requiredSkill: 'foraging',
    harvestTime: 5,
    maxHarvests: 3,
    drops: [
      { itemId: 'mushroom', chance: 0.85, minQuantity: 2, maxQuantity: 4 },
      { itemId: 'rare_mushroom', chance: 0.15, minQuantity: 1, maxQuantity: 1 },
    ],
  },
  rare_flower_bed: {
    name: 'Rare Flower Bed',
    description: 'A bed of exotic and rare flowers.',
    requiredLevel: 30,
    requiredSkill: 'foraging',
    harvestTime: 6,
    maxHarvests: 2,
    drops: [
      { itemId: 'rare_flower', chance: 0.7, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'fiber', chance: 0.5, minQuantity: 1, maxQuantity: 2 },
    ],
  },
};

// Inject shimmer animation styles
const injectStyles = (): void => {
  const styleId = 'resource-research-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    .research-shimmer {
      background: linear-gradient(90deg, #161b22 0%, #1c2128 50%, #161b22 100%);
      background-size: 200% 100%;
      animation: shimmer 2s infinite;
    }
  `;
  document.head.appendChild(style);
};

// ============ COMPONENT ============

export const ResourceNodeResearchPanel: React.FC<ResourceNodeResearchPanelProps> = ({
  isOpen,
  onClose,
  nodeId,
  nodeType,
  nodePosition,
  playerPosition,
}) => {
  const [isResearching, setIsResearching] = useState<boolean>(false);
  const [researchProgress, setResearchProgress] = useState<number>(0);
  const [researchData, setResearchData] = useState<ResourceNodeResearchData | null>(null);

  // Inject styles on mount
  useEffect(() => {
    injectStyles();
  }, []);

  // Reset state when panel opens/closes or node changes
  useEffect(() => {
    if (isOpen && nodeId && nodeType) {
      setIsResearching(false);
      setResearchProgress(0);
      setResearchData(null);
    }
  }, [isOpen, nodeId, nodeType]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Generate research data from node type
  const generateResearchData = useCallback((): void => {
    if (!nodeId || !nodeType) return;

    const nodeInfo = RESOURCE_NODE_INFO[nodeType];
    if (!nodeInfo) return;

    setResearchData({
      nodeId,
      nodeType,
      name: nodeInfo.name,
      description: nodeInfo.description,
      requiredLevel: nodeInfo.requiredLevel,
      requiredSkill: nodeInfo.requiredSkill,
      harvestTime: nodeInfo.harvestTime,
      maxHarvests: nodeInfo.maxHarvests,
      drops: nodeInfo.drops,
    });
  }, [nodeId, nodeType]);

  // Start research process
  const startResearch = useCallback((): void => {
    if (isResearching || researchData) return;

    // Play research start SFX
    const soundManager = getSoundManager();
    soundManager?.play(SFX_KEYS.RESEARCH_START);

    setIsResearching(true);
    setResearchProgress(0);

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(100, (elapsed / RESEARCH_DURATION_MS) * 100);
      setResearchProgress(progress);

      if (progress >= 100) {
        clearInterval(interval);
        setIsResearching(false);
        // Play research complete SFX
        soundManager?.play(SFX_KEYS.RESEARCH_COMPLETE);
        generateResearchData();
      }
    }, 50);
  }, [isResearching, researchData, generateResearchData]);

  // Cancel research if panel closes during research
  useEffect(() => {
    if (!isOpen && isResearching) {
      setIsResearching(false);
      setResearchProgress(0);
    }
  }, [isOpen, isResearching]);

  if (!isOpen || !nodeId || !nodeType) return null;

  const nodeInfo = RESOURCE_NODE_INFO[nodeType];
  const skillName = nodeInfo ? (nodeInfo.requiredSkill.charAt(0).toUpperCase() + nodeInfo.requiredSkill.slice(1)) : 'Unknown';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#0d1117',
          border: '2px solid #30363d',
          padding: 0,
          minWidth: '300px',
          maxWidth: '380px',
          fontFamily: '"Sometype Mono", monospace',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          <span style={{ color: '#5dade2', fontSize: '14px', fontWeight: 'bold' }}>
            RESOURCE NODE ANALYSIS
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #f85149',
              color: '#f85149',
              cursor: 'pointer',
              fontSize: '14px',
              padding: '4px 8px',
              fontFamily: '"Sometype Mono", monospace',
              fontWeight: 'bold',
            }}
          >
            x
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px' }}>
          {/* Not yet researched */}
          {!researchData && !isResearching && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <div style={{
                width: '64px',
                height: '64px',
                flexShrink: 0,
                border: '2px dashed #5dade2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <span style={{ color: '#5dade2', fontSize: '28px', fontWeight: 'bold' }}>?</span>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#8b949e', fontSize: '12px', margin: '0 0 12px 0' }}>
                  An unidentified resource node. Research it to discover its properties.
                </p>
                <button
                  onClick={startResearch}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#238636',
                    border: '1px solid #2ea043',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    fontFamily: '"Sometype Mono", monospace',
                  }}
                >
                  RESEARCH
                </button>
              </div>
            </div>
          )}

          {/* Researching */}
          {isResearching && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{
                width: '100%',
                height: '24px',
                backgroundColor: '#161b22',
                border: '1px solid #30363d',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div
                  className="research-shimmer"
                  style={{
                    width: `${researchProgress}%`,
                    height: '100%',
                    transition: 'width 0.1s linear',
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: '#ffffff',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  textShadow: '0 0 4px rgba(0,0,0,0.8)',
                }}>
                  RESEARCHING... {Math.round(researchProgress)}%
                </div>
              </div>
              <p style={{ color: '#8b949e', fontSize: '11px', textAlign: 'center', margin: 0 }}>
                Analyzing resource composition...
              </p>
            </div>
          )}

          {/* Researched - show data */}
          {researchData && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Node Name */}
              <div>
                <h3 style={{ color: '#58a6ff', fontSize: '16px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
                  {researchData.name}
                </h3>
                <p style={{ color: '#8b949e', fontSize: '12px', margin: 0, lineHeight: '1.5' }}>
                  {researchData.description}
                </p>
              </div>

              {/* Requirements */}
              <div style={{ padding: '12px', backgroundColor: '#161b22', border: '1px solid #30363d' }}>
                <div style={{ color: '#8b949e', fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase' }}>
                  Requirements
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#ffffff', fontSize: '12px' }}>
                    {skillName} Level {researchData.requiredLevel}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                  <span style={{ color: '#8b949e', fontSize: '11px' }}>Harvest Time:</span>
                  <span style={{ color: '#ffffff', fontSize: '11px' }}>{researchData.harvestTime}s</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                  <span style={{ color: '#8b949e', fontSize: '11px' }}>Max Harvests:</span>
                  <span style={{ color: '#ffffff', fontSize: '11px' }}>{researchData.maxHarvests}</span>
                </div>
              </div>

              {/* Drops */}
              <div>
                <div style={{ color: '#8b949e', fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase' }}>
                  Possible Drops
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {researchData.drops.map((drop, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '8px',
                        backgroundColor: '#161b22',
                        border: '1px solid #30363d',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ color: '#ffffff', fontSize: '12px' }}>
                        {drop.itemId.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ color: '#8b949e', fontSize: '11px' }}>
                          {drop.minQuantity === drop.maxQuantity ? `${drop.minQuantity}` : `${drop.minQuantity}-${drop.maxQuantity}`}
                        </span>
                        <span style={{ color: '#58a6ff', fontSize: '11px' }}>
                          {(drop.chance * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

